import React from 'react';
import type { Session } from '../session.js';
import { AgentBanner } from './AgentBanner.js';
// THROWAWAY — bug reporter widget, remove before go-live (P12)
import { BugReporter } from '../dev/BugReporter.js';

interface AppLayoutProps {
  session: Session | undefined;
  onClearSession: () => void;
  children: React.ReactNode;
  /**
   * Admin Console banner strip (#302 follow-up, 2026-07-09 — Dylan: on the
   * Admin Console page ONLY, the top banner must show ONLY the logo — no
   * customer information, no interaction-state pill, no Clear session
   * button). Callers (App.tsx) set this from the current route; every other
   * screen renders the full banner (default `false`).
   */
  logoOnly?: boolean;
  /** #430 — surface the banner "Connect to Genesys" button (forwarded to AgentBanner). */
  showConnectGenesys?: boolean;
  /** #430 — pulse the Connect button (only on a live Online interaction). */
  connectGenesysPulse?: boolean;
  /** #430 — opens the Genesys link popup from the banner Connect button. */
  onConnectGenesys?: () => void;
  /**
   * #715 — forwarded to AgentBanner. When provided, renders a discrete "Log
   * out" link beneath the Clear session button. Only supplied on the ID&V and
   * Customer Search pages (see App.tsx).
   */
  onLogout?: () => void;
}

/**
 * The width every screen is designed at. The banner and the widest screens
 * (tables, dropdowns, the security pages) all target `max-w-[1800px]`, so 1800
 * is the natural design width for the scale-to-fit canvas below.
 */
export const DESIGN_WIDTH = 1800;

/**
 * Upper bound on the zoom factor. The target environment is 1920×1200 inside a
 * Genesys agent-script iframe; at 1920px the design (1800px) would scale to
 * ~1.067, so we allow scaling *up* to fill the iframe. We cap at 1.15 so an
 * unusually wide container can't blow the UI up to an awkward size — beyond
 * that we simply centre with a little whitespace rather than keep zooming.
 */
const MAX_SCALE = 1.15;

/**
 * A resize smaller than this (in CSS px) does not update the measured width, so
 * it cannot retrigger the zoom recompute. This suppresses the "screen shaking"
 * (#560) scrollbar-driven oscillation as a second line of defence behind the
 * `scrollbar-gutter: stable` gutter reservation in `theme.css`: even where the
 * gutter isn't honoured (e.g. some overlay-scrollbar platforms), a vertical
 * scrollbar toggling only changes the width by ~its own thickness (≤ ~17px),
 * which is below this threshold — so the feedback loop (narrower width → smaller
 * zoom → scrollbar disappears → wider width → …) cannot start. It is comfortably
 * below any real window/iframe resize, so genuine resizes still take effect.
 */
const RESIZE_EPSILON_PX = 24;

/**
 * Measures the width of `ref` (the true viewport / iframe width) and keeps it
 * in state, updating on resize via a ResizeObserver plus an initial measure.
 *
 * Sub-{@link RESIZE_EPSILON_PX} width changes are ignored so a toggling vertical
 * scrollbar can't drive a zoom-recompute oscillation (#560) — see the constant.
 */
function useContainerWidth(
  ref: React.RefObject<HTMLElement | null>,
): number | undefined {
  const [width, setWidth] = React.useState<number | undefined>(undefined);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = (): void => {
      const next = el.clientWidth;
      setWidth((prev) =>
        // Always accept the first measurement; afterwards ignore tiny deltas
        // (a scrollbar appearing/disappearing) that would otherwise oscillate.
        prev === undefined || Math.abs(next - prev) >= RESIZE_EPSILON_PX
          ? next
          : prev,
      );
    };
    measure();

    if (typeof ResizeObserver === 'undefined') {
      // Fallback for environments without ResizeObserver (e.g. jsdom).
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return width;
}

/**
 * Online/Offline-aware app shell. Renders the persistent agent banner above the
 * active screen.
 *
 * **Scale-to-fit (issue #253):** rather than reflowing the layout as the window
 * narrows, we scale the *entire* UI (text, spacing, controls together) by a
 * single factor so the fixed {@link DESIGN_WIDTH} design always fits the actual
 * container/iframe width — no reflow, no reshaping, no horizontal scrollbar, no
 * wasted whitespace. `factor = containerWidth / DESIGN_WIDTH`, capped at
 * {@link MAX_SCALE}.
 *
 * We scale with CSS **`zoom`**, not `transform: scale()`. A `transform` only
 * moves the element's *visual* box while leaving its *layout* box unscaled, so
 * the browser can neither reserve the correct height nor scroll a transformed,
 * overflowing element into view — which broke Playwright actionability on long
 * flows (e.g. the card-maintenance PIN-reminder confirmation and its checkbox
 * fell "outside the viewport" and could not be clicked/seen). `zoom` scales the
 * layout box *and* the visuals uniformly, so it looks identical (a uniform
 * geometric zoom of the frozen 1800px layout — nothing re-wraps, nothing drops
 * underneath) while participating in real layout: hit-testing,
 * `getBoundingClientRect`, native scrolling and Playwright actionability all
 * behave correctly. Because `zoom` reserves the correct scaled box, no
 * height-reservation or overflow hacks are needed. Chromium (which
 * browser-E2E runs on) supports `zoom` fully. Screens own their own page
 * layout; zoom does not touch the DOM/text, so all existing role/text locators
 * stay valid.
 */
export function AppLayout({
  session,
  onClearSession,
  children,
  logoOnly = false,
  showConnectGenesys = false,
  connectGenesysPulse = false,
  onConnectGenesys,
  onLogout,
}: AppLayoutProps): React.JSX.Element {
  const outerRef = React.useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(outerRef);

  // Scale so the design width fits the container; allow zooming up to MAX_SCALE
  // (so 1920px iframes fill), otherwise scale down to fit.
  //
  // Snap to exactly 1 when the container is within a scrollbar's width
  // ({@link RESIZE_EPSILON_PX}) of the design width. The `scrollbar-gutter:
  // stable` reservation (#560, theme.css) permanently shaves the scrollbar's
  // thickness off `clientWidth`, so a container that is *nominally* the design
  // width measures ~15px narrower — which would otherwise render at a fractional
  // `zoom` (~0.992) rather than a clean 1:1. Snapping keeps such a container at
  // `zoom: 1`: a container a scrollbar narrower than the design should render
  // one-to-one, not slightly shrunk. This also restores the deterministic
  // `zoom: 1` the browser-E2E relies on (it drives the viewport at DESIGN_WIDTH,
  // now gutter-shaved) — Playwright's actionability math is knownly wrong under
  // any `zoom != 1` (#253). Uses the same epsilon as the resize filter: both
  // express "changes within a scrollbar's width don't move the zoom".
  const rawScale =
    containerWidth === undefined
      ? 1
      : Math.min(containerWidth / DESIGN_WIDTH, MAX_SCALE);
  const scale =
    containerWidth !== undefined &&
    Math.abs(containerWidth - DESIGN_WIDTH) < RESIZE_EPSILON_PX
      ? 1
      : rawScale;

  return (
    <div
      ref={outerRef}
      data-testid="app-scale-viewport"
      // True iframe/viewport width. `zoom` on the inner track scales its layout
      // box too, so the track never exceeds this width and no horizontal
      // scrollbar appears — we don't need an overflow clip or a reserved
      // height (both were `transform`-only workarounds).
      className={`min-h-screen bg-[#f9fafb] app-shell--${
        session?.state ?? 'offline'
      }`}
    >
      <div
        data-testid="app-scale-track"
        // `zoom` scales layout + visuals uniformly and participates in real
        // layout (unlike `transform: scale()`), so scroll/hit-testing/actionability
        // stay correct. `zoom` is a non-standard property, so it is not in the
        // typed CSSProperties — cast to set it.
        style={
          {
            width: DESIGN_WIDTH,
            zoom: scale,
          } as React.CSSProperties
        }
      >
        <AgentBanner
          session={session}
          onClearSession={onClearSession}
          logoOnly={logoOnly}
          showConnectGenesys={showConnectGenesys}
          connectGenesysPulse={connectGenesysPulse}
          onConnectGenesys={onConnectGenesys}
          onLogout={onLogout}
        />
        {children}
      </div>
      {/* THROWAWAY — bug reporter widget, remove before go-live (P12). Single
          mount point: delete this line + dev/BugReporter.tsx +
          server/src/dev/bug-report.ts + bug-allowlist.ts (and the staging
          env/secret wiring) to fully remove the feature. Visibility is purely
          runtime (server bugReporterEnabled flag + allow-list, staging only).
          Kept OUTSIDE the scaled track so the dev widget stays at native size. */}
      <BugReporter />
    </div>
  );
}
